// 全局家庭数据：records + stats + me
// 特性：sessionStorage 缓存首次加载 + 静默拉新 + 无差异不重渲染

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { api } from '../lib/api'
import type { BabyRecord, Me, StatsResponse } from '../lib/types'

interface FamilyData {
  records: BabyRecord[]
  stats: StatsResponse | null
  me: Me | null
  loaded: boolean
  refreshing: boolean
  refresh: () => Promise<void>
  setRecords: (records: BabyRecord[]) => void
  refreshMe: () => Promise<void>
}

const STORAGE_KEY = 'baby-log:cache:v1'

interface CachedData {
  records: BabyRecord[]
  stats: StatsResponse | null
  me: Me | null
  cachedAt: number
}

function loadCache(): CachedData | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY)
    if (v) return JSON.parse(v) as CachedData
  } catch {}
  return null
}

function saveCache(data: CachedData) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {}
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== typeof b) return false
  if (typeof a === 'object') {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  return false
}

const FamilyDataContext = createContext<FamilyData | null>(null)

export function FamilyDataProvider({ children }: { children: ReactNode }) {
  const cached = loadCache()
  const [records, setRecordsState] = useState<BabyRecord[] | null>(cached?.records ?? null)
  const [stats, setStats] = useState<StatsResponse | null>(cached?.stats ?? null)
  const [me, setMe] = useState<Me | null>(cached?.me ?? null)
  const [refreshing, setRefreshing] = useState(false)

  const isFirstLoad = records === null

  // 首次加载：显示 loading（虽然有缓存但仍快速拉新）
  const refresh = useCallback(async () => {
    if (isFirstLoad) return // 由首次 effect 处理
    setRefreshing(true)
    try {
      const [listRes, statsRes, meRes] = await Promise.all([
        api.listRecords(),
        api.stats(7),
        api.me(),
      ])
      const newRecords = listRes.records
      const newStats = statsRes
      const newMe = meRes
      if (!deepEqual(newRecords, records)) setRecordsState(newRecords)
      if (!deepEqual(newStats, stats)) setStats(newStats)
      if (!deepEqual(newMe, me)) setMe(newMe)
      saveCache({ records: newRecords, stats: newStats, me: newMe, cachedAt: Date.now() })
    } catch {
      // ignore
    } finally {
      setRefreshing(false)
    }
  }, [isFirstLoad, records, stats, me])

  const refreshMe = useCallback(async () => {
    try {
      const meRes = await api.me()
      if (meRes && !deepEqual(meRes, me)) {
        setMe(meRes)
        saveCache({ records: records ?? [], stats, me: meRes, cachedAt: Date.now() })
      }
    } catch {}
  }, [records, stats, me])

  const setRecords = useCallback((next: BabyRecord[]) => {
    setRecordsState(next)
    saveCache({ records: next, stats, me, cachedAt: Date.now() })
  }, [stats, me])

  // 首次加载：拉全部
  useEffect(() => {
    if (!isFirstLoad) return
    let mounted = true
    ;(async () => {
      try {
        const [listRes, statsRes, meRes] = await Promise.all([
          api.listRecords(),
          api.stats(7),
          api.me(),
        ])
        if (!mounted) return
        setRecordsState(listRes.records)
        setStats(statsRes)
        setMe(meRes)
        saveCache({
          records: listRes.records,
          stats: statsRes,
          me: meRes,
          cachedAt: Date.now(),
        })
      } catch {
        if (!mounted) return
        setRecordsState([])
      }
    })()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 30s 静默轮询
  useEffect(() => {
    if (isFirstLoad) return
    const t = window.setInterval(() => {
      refresh()
    }, 30000)
    return () => window.clearInterval(t)
  }, [isFirstLoad, refresh])

  return (
    <FamilyDataContext.Provider
      value={{
        records: records ?? [],
        stats,
        me,
        loaded: !isFirstLoad,
        refreshing,
        refresh,
        setRecords,
        refreshMe,
      }}
    >
      {children}
    </FamilyDataContext.Provider>
  )
}

export function useFamilyData(): FamilyData {
  const ctx = useContext(FamilyDataContext)
  if (!ctx) throw new Error('useFamilyData must be used within FamilyDataProvider')
  return ctx
}
